import React, { useState, useEffect } from 'react';
import { DeliveryChallan, DeliveryChallanItem } from '../types/billing';

interface DeliveryChallanFormProps {
  onSave: (challan: Omit<DeliveryChallan, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  onCancel: () => void;
  initialData?: Partial<DeliveryChallan>;
  isEdit?: boolean;
}

const DeliveryChallanForm: React.FC<DeliveryChallanFormProps> = ({
  onSave,
  onCancel,
  initialData,
  isEdit = false
}) => {
  const [formData, setFormData] = useState({
    challanNumber: initialData?.challanNumber || '',
    challanDate: initialData?.challanDate || new Date().toISOString().split('T')[0],
    employeeName: initialData?.employeeName || '',
    employeeId: initialData?.employeeId || '',
    purpose: initialData?.purpose || '',
    expectedReturnDate: initialData?.expectedReturnDate || '',
    issuedBy: initialData?.issuedBy || '',
    receivedBy: initialData?.receivedBy || '',
    notes: initialData?.notes || '',
    createdBy: initialData?.createdBy || '',
    companyId: initialData?.companyId || '',
  });

  const [items, setItems] = useState<DeliveryChallanItem[]>(
    initialData?.items || [{ sn: 1, description: '', qty: 1, remark: '' }]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        challanNumber: initialData.challanNumber || '',
        challanDate: initialData.challanDate || new Date().toISOString().split('T')[0],
        employeeName: initialData.employeeName || '',
        employeeId: initialData.employeeId || '',
        purpose: initialData.purpose || '',
        expectedReturnDate: initialData.expectedReturnDate || '',
        issuedBy: initialData.issuedBy || '',
        receivedBy: initialData.receivedBy || '',
        notes: initialData.notes || '',
        createdBy: initialData.createdBy || '',
        companyId: initialData.companyId || '',
      });
      setItems(initialData.items || [{ sn: 1, description: '', qty: 1, remark: '' }]);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.employeeName || !formData.purpose || !formData.issuedBy || !formData.receivedBy) {
      alert('Please fill in all required fields');
      return;
    }

    if (items.length === 0 || items.some(item => !item.description)) {
      alert('Please add at least one item with a description');
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      const challan: Omit<DeliveryChallan, 'id' | 'createdAt' | 'updatedAt'> = {
        challanNumber: formData.challanNumber,
        challanDate: formData.challanDate,
        employeeName: formData.employeeName,
        employeeId: formData.employeeId || undefined,
        purpose: formData.purpose,
        expectedReturnDate: formData.expectedReturnDate || undefined,
        items: items.filter(item => item.description.trim() !== ''), // Remove empty items
        issuedBy: formData.issuedBy,
        receivedBy: formData.receivedBy,
        status: 'Active',
        notes: formData.notes || undefined,
        createdBy: formData.createdBy || 'Admin',
        companyId: formData.companyId || undefined,
      };

      await onSave(challan);
      setSuccessMessage(isEdit ? 'Delivery challan updated successfully!' : 'Delivery challan created successfully!');

      // Auto-close after a brief delay to show success message
      setTimeout(() => {
        onCancel();
      }, 1500);

    } catch (error) {
      console.error('Error saving delivery challan:', error);
      alert('Error saving delivery challan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index: number, field: keyof DeliveryChallanItem, value: string | number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setItems(updatedItems);
  };

  const addItem = () => {
    const newSn = items.length > 0 ? Math.max(...items.map(item => item.sn)) + 1 : 1;
    setItems([...items, { sn: newSn, description: '', qty: 1, remark: '' }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      const updatedItems = items.filter((_, i) => i !== index);
      // Re-number the items
      setItems(updatedItems.map((item, i) => ({ ...item, sn: i + 1 })));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white max-h-screen overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {isEdit ? 'Edit Delivery Challan' : 'Create Delivery Challan'}
            </h3>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Success Message */}
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
                {successMessage}
              </div>
            )}

            {/* Challan Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Challan Number *
                </label>
                <input
                  type="text"
                  name="challanNumber"
                  value={formData.challanNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  readOnly={isEdit} // Don't allow editing challan number in edit mode
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Challan Date *
                </label>
                <input
                  type="date"
                  name="challanDate"
                  value={formData.challanDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Name *
                </label>
                <input
                  type="text"
                  name="employeeName"
                  value={formData.employeeName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Employee taking the items"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company ID
                </label>
                <input
                  type="text"
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Company ID (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee ID
                </label>
                <input
                  type="text"
                  name="employeeId"
                  value={formData.employeeId}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Employee ID (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose *
                </label>
                <input
                  type="text"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Purpose of taking items"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Return Date
                </label>
                <input
                  type="date"
                  name="expectedReturnDate"
                  value={formData.expectedReturnDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Issued By *
                </label>
                <input
                  type="text"
                  name="issuedBy"
                  value={formData.issuedBy}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Person issuing the challan"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Received By *
                </label>
                <input
                  type="text"
                  name="receivedBy"
                  value={formData.receivedBy}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Employee acknowledgment"
                  required
                />
              </div>
            </div>

            {/* Items Table */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Items/Instruments</h4>
                <button
                  type="button"
                  onClick={addItem}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Add Item
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.N.
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description *
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Remarks
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {item.sn}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                            placeholder="Item description"
                            required
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="number"
                            value={item.qty}
                            onChange={(e) => handleItemChange(index, 'qty', parseInt(e.target.value) || 1)}
                            min="1"
                            className="w-32 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="text"
                            value={item.remark || ''}
                            onChange={(e) => handleItemChange(index, 'remark', e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                            placeholder="Additional remarks"
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                          {items.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-900 mr-2"
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional notes or instructions"
              />
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : (isEdit ? 'Update Challan' : 'Create Challan')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeliveryChallanForm;

